import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Table,
	Tabs,
	Upload,
	UploadFile,
} from 'antd';
import {
	DeleteOutlined,
	EditOutlined,
	FileSearchOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { formatCurrency } from '@/libs/antd/formater';
import { FC, Fragment, useState } from 'react';
import TextArea from 'antd/es/input/TextArea';
import rule from '@/libs/antd/rules';
import { useForm } from '@/libs/antd/form';
import {
	getNAPAnalysis,
	NAPAnalysis,
	NAPAnalysisFinancialCategory,
	NAPAnalysisLegalAndManagementCategory,
	NAPAnalysisMarketingCategory,
	NAPAnalysisPayload,
	NAPAnalysisTechnicalCategory,
	submitNAPAnalysis,
	verifyNAPAnalysis,
} from '@/libs/fleet-api/services/nap';
import { useModals } from '@/components/modals/ModalProvider';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FleetFile } from '@/libs/fleet-api/fetcher';
import { FileData } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import ManagerProfileModal, {
	useManagerProfileModal,
} from './modals/ManagerProfileModal';
import LegalDocumentModal, {
	useLegalDocumentModal,
} from './modals/LegalDocumentModal';
import TradeCheckingSupplierModal, {
	useTradeCheckingSupplierModal,
} from './modals/TradeCheckingSupplierModal';
import EmploymentContractModal, {
	useEmploymentContractModal,
} from './modals/EmploymentContractModal';
import TradeCheckingBouwheerModal, {
	useTradeCheckingBouwheerModal,
} from './modals/TradeCheckingBouwheerModal';
import KAPModal, { useKAPModal } from './modals/KAPModal';
import { useUserData } from '@/libs/fleet-api/hooks';
import BusinessDocumentModal, {
	useBusinessDocumentModal,
} from './modals/BussinessDocumentModal';

type NAPAnalysisForm = Omit<
	NAPAnalysis,
	| 'legalAndManagementCategory'
	| 'marketingCategory'
	| 'technicalCategory'
	| 'financialCategory'
> & {
	legalAndManagementCategory: NAPAnalysisLegalAndManagementCategory;
	technicalCategory: NAPAnalysisTechnicalCategory;
	marketingCategory: NAPAnalysisMarketingCategory;
	financialCategory: NAPAnalysisFinancialCategory;
};

const NAPAnalysisForm: FC<{
	// napState?: NAPState;
	prospectId: string;
	customerId: string;
	onSubmitted: () => void;
}> = ({ prospectId, customerId, onSubmitted }) => {
	const { groupName } = useUserData() ?? {};
	const { form, value, fieldName } = useForm<NAPAnalysisForm>();

	const [activeKey, setActiveKey] = useState('1');
	const uploadMutation = useMutation({
		mutationFn: uploader,
	});
	const submitNAPAnalysisMutation = useMutation({
		mutationFn: submitNAPAnalysis,
	});
	const verifyNAPAnalysisMutation = useMutation({
		mutationFn: verifyNAPAnalysis,
	});
	const getNAPAnalysisQuery = useQuery({
		queryKey: ['getNAPAnalysis', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPAnalysis(prospectId);
		},
	});

	// Modals
	const { openPreviewModal } = useModals();
	const managerProfileModal = useManagerProfileModal((type, payload) => {
		if (type === 'edit') {
			form.setFieldValue(
				fieldName(['legalAndManagementCategory', 'customerManagements']),
				value?.legalAndManagementCategory?.customerManagements?.map(
					(data, index) => {
						return index === payload.index ? payload : data;
					},
				),
			);
		}
	});
	const legalDocumentModal = useLegalDocumentModal();
	const businessDocumentModal = useBusinessDocumentModal();
	const tradeCheckingSupplierModal = useTradeCheckingSupplierModal();
	const employmentContractModal = useEmploymentContractModal();
	const tradeCheckingBouwheerModal = useTradeCheckingBouwheerModal();
	const kapModal = useKAPModal();

	const { data } = getNAPAnalysisQuery.data ?? {};
	// const isReadOnly =
	// 	napState?.action === 'VIEW' || napState?.action === 'DISABLED';

	if (getNAPAnalysisQuery.isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	const handleNext = async (nextTabKey: string) => {
		console.log(nextTabKey);
		try {
			await form.validateFields();
			if (nextTabKey === 'submit') {
				form.submit();
			} else {
				setActiveKey(nextTabKey);
			}
		} catch (error) {
			console.log(error);
			// Find the first tab with validation errors
			// const errorFields = (error as { errorFields?: { name: string[] }[] })
			// 	.errorFields; // Specify a more precise type
			// if (errorFields && errorFields.length > 0) {
			// 	const firstErrorField = errorFields[0].name[0];
			// 	if (firstErrorField.startsWith('legalAndManagementCategory')) {
			// 		setActiveKey('1');
			// 	} else if (firstErrorField.startsWith('technicalCategory')) {
			// 		setActiveKey('2');
			// 	} else if (firstErrorField.startsWith('marketingCategory')) {
			// 		setActiveKey('3');
			// 	} else if (firstErrorField.startsWith('financialCategory')) {
			// 		setActiveKey('4');
			// 	}
			// }
		}
	};

	// LEGAL MANAGEMENT
	const LMcompanyLegalFrag = () => {
		const isReadOnly =
			data?.legalAndManagementCategory.action === 'View' ||
			data?.legalAndManagementCategory.action === 'Disabled';

		return (
			<Fragment>
				<Table
					style={{ marginTop: 20, marginBottom: 20 }}
					scroll={{ x: 'max-content' }}
					bordered
					pagination={false}
					dataSource={
						value?.legalAndManagementCategory?.customerLegalDocuments?.map(
							(data, i) => {
								return {
									...data,
									key: i + 1,
								};
							},
						) ?? []
					}
					columns={[
						{
							title: 'Action',
							colSpan: 2,
							width: 100,
							align: 'center',
							render: (_, record) => (
								<a
									onClick={(e) => {
										e.preventDefault();
										if (!record.file?.id) return;
										openPreviewModal(record.file as FleetFile);
									}}
									style={!record.file?.id ? { color: '#bababa' } : {}}
								>
									<FileSearchOutlined />
								</a>
							),
						},
						{
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									style={
										isReadOnly
											? { color: '#bababa' }
											: groupName === 'FRM' && record.disableOnFrmNap
												? { color: '#bababa' }
												: {}
									}
									onClick={async (e) => {
										e.preventDefault();
										if (
											isReadOnly ||
											(groupName === 'FRM' ? record.disableOnFrmNap : false)
										)
											return;

										const editData = await legalDocumentModal.open(record);
										if (!editData) return;

										form.setFieldValue(
											fieldName([
												'legalAndManagementCategory',
												'customerLegalDocuments',
											]),
											value?.legalAndManagementCategory?.customerLegalDocuments?.map(
												(data, dataIndex) => {
													return index === dataIndex ? editData : data;
												},
											),
										);
									}}
								>
									<EditOutlined />
								</a>
							),
						},
						{
							title: 'Jenis Dokumen',
							width: 350,
							render: (_, record) => record.documentType,
						},
						{
							title: 'No Dokumen',
							render: (_, record) => record.documentNo,
						},
						{
							title: 'Tanggal Dokumen',
							width: 100,
							render: (_, record) =>
								record.documentDate &&
								dayjs(record.documentDate).format('DD/MM/YYYY'),
						},
						{
							title: 'Keterangan',
							render: (_, record) => record.notes,
						},
					]}
				/>
			</Fragment>
		);
	};

	const LMlicensingLegalFrag = () => {
		const isReadOnly =
			data?.legalAndManagementCategory.action === 'View' ||
			data?.legalAndManagementCategory.action === 'Disabled';

		return (
			<Fragment>
				<Table
					style={{ marginTop: 20, marginBottom: 20 }}
					scroll={{ x: 'max-content' }}
					bordered
					pagination={false}
					dataSource={
						value?.legalAndManagementCategory?.customerBusinessDocuments?.map(
							(data, i) => {
								return {
									...data,
									key: i + 1,
								};
							},
						) ?? []
					}
					columns={[
						{
							title: 'Action',
							colSpan: 2,
							width: 100,
							align: 'center',
							render: (_, record) => (
								<a
									onClick={(e) => {
										e.preventDefault();
										if (!record.file?.id) return;
										openPreviewModal(record.file as FleetFile);
									}}
									style={!record.file?.id ? { color: '#bababa' } : {}}
								>
									<FileSearchOutlined />
								</a>
							),
						},
						{
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={async (e) => {
										e.preventDefault();
										if (isReadOnly) return;

										const editData = await businessDocumentModal.open(record);
										if (!editData) return;

										form.setFieldValue(
											fieldName([
												'legalAndManagementCategory',
												'customerBusinessDocuments',
											]),
											value?.legalAndManagementCategory?.customerBusinessDocuments?.map(
												(data, dataIndex) => {
													return index === dataIndex ? editData : data;
												},
											),
										);
									}}
								>
									<EditOutlined />
								</a>
							),
						},
						{
							title: 'Jenis Dokumen',
							width: 350,
							render: (_, record) => record.documentType,
						},
						{
							title: 'No Dokumen',
							render: (_, record) => record.documentNo,
						},
						{
							title: 'Tanggal Dokumen',
							width: 100,
							render: (_, record) =>
								record.documentDate &&
								dayjs(record.documentDate).format('DD/MM/YYYY'),
						},
						{
							title: 'Keterangan',
							render: (_, record) => record.notes,
						},
					]}
				/>
			</Fragment>
		);
	};

	const LMguarantorLegalFrag = () => {
		const isReadOnly =
			data?.legalAndManagementCategory.action === 'View' ||
			data?.legalAndManagementCategory.action === 'Disabled';

		return (
			<Fragment>
				<Table
					style={{ marginTop: 20, marginBottom: 20 }}
					scroll={{ x: 'max-content' }}
					bordered
					pagination={false}
					dataSource={
						value?.legalAndManagementCategory?.guarantorCompanyLegalityDocuments?.map(
							(data, i) => {
								return {
									...data,
									key: i + 1,
								};
							},
						) ?? []
					}
					columns={[
						{
							title: 'Action',
							colSpan: 2,
							width: 100,
							align: 'center',
							render: (_, record) => (
								<a
									onClick={(e) => {
										e.preventDefault();
										if (!record.file?.id) return;
										openPreviewModal(record.file as FleetFile);
									}}
									style={!record.file?.id ? { color: '#bababa' } : {}}
								>
									<FileSearchOutlined />
								</a>
							),
						},
						{
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={async (e) => {
										e.preventDefault();
										if (isReadOnly) return;

										const editData = await legalDocumentModal.open(record);
										if (!editData) return;

										form.setFieldValue(
											fieldName([
												'legalAndManagementCategory',
												'guarantorCompanyLegalityDocuments',
											]),
											value?.legalAndManagementCategory?.guarantorCompanyLegalityDocuments?.map(
												(data, dataIndex) => {
													return index === dataIndex ? editData : data;
												},
											),
										);
									}}
								>
									<EditOutlined />
								</a>
							),
						},
						{
							title: 'Jenis Dokumen',
							render: (_, record) => record.documentType,
						},
						{
							title: 'No Dokumen',
							render: (_, record) => record.documentNo,
						},
						{
							title: 'Tanggal Dokumen',
							render: (_, record) =>
								record.documentDate &&
								dayjs(record.documentDate).format('DD/MM/YYYY'),
						},
						{
							title: 'Keterangan',
							render: (_, record) => record.notes,
						},
					]}
				/>
			</Fragment>
		);
	};

	const LMmanagerProfileFrag = () => {
		const isReadOnly =
			data?.legalAndManagementCategory.action === 'View' ||
			data?.legalAndManagementCategory.action === 'Disabled';

		return (
			<Fragment>
				<Table
					style={{ marginTop: 20, marginBottom: 20 }}
					scroll={{ x: 'max-content' }}
					bordered
					pagination={false}
					dataSource={
						value?.legalAndManagementCategory?.customerManagements?.map(
							(data, i) => {
								return {
									...data,
									key: i + 1,
								};
							},
						) ?? []
					}
					columns={[
						{
							title: 'Action',
							colSpan: 2,
							width: 100,
							align: 'center',
							render: (_, record) => (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!record.identityFile?.id) return;
										openPreviewModal(record.identityFile as FleetFile);
									}}
									style={!record.identityFile?.id ? { color: '#bababa' } : {}}
								>
									<FileSearchOutlined />
								</a>
							),
						},
						{
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={(e) => {
										e.preventDefault();
										if (!isReadOnly) {
											managerProfileModal.open({
												type: 'edit',
												data: {
													...record,
													index,
												},
											});
										}
									}}
								>
									<EditOutlined />
								</a>
							),
						},
						{
							title: 'Nama',
							render: (_, record) => record.name,
						},
						{
							title: 'Jenis Identitas',
							render: (_, record) => record.identityCardType.value,
						},
						{
							title: 'No. ID',
							render: (_, record) => record.identityNumber,
						},
						{
							title: 'Periode Kepengurusan',
							render: (_, record) =>
								record.startManagementPeriod &&
								record.endManagementPeriod &&
								`${dayjs(record.startManagementPeriod).format('DD/MM/YYYY')} - ${dayjs(record.endManagementPeriod).format('DD/MM/YYYY')}`,
						},
						{
							title: 'Pengalaman Professional',
							render: (_, record) => record.profesionalExperience,
						},
					]}
				/>
			</Fragment>
		);
	};

	const LegalManagementFrag = () => {
		const isReadOnly =
			data?.legalAndManagementCategory.action === 'View' ||
			data?.legalAndManagementCategory.action === 'Disabled';

		return (
			<Fragment>
				<Form.Item
					name={fieldName([
						'legalAndManagementCategory',
						'customerLegalDocuments',
					])}
					initialValue={
						data?.legalAndManagementCategory?.customerLegalDocuments ?? []
					}
					noStyle
				/>
				<Form.Item
					name={fieldName([
						'legalAndManagementCategory',
						'customerBusinessDocuments',
					])}
					initialValue={
						data?.legalAndManagementCategory?.customerBusinessDocuments ?? []
					}
					noStyle
				/>
				<Form.Item
					name={fieldName([
						'legalAndManagementCategory',
						'guarantorCompanyLegalityDocuments',
					])}
					initialValue={
						data?.legalAndManagementCategory
							?.guarantorCompanyLegalityDocuments ?? []
					}
					noStyle
				/>
				<Form.Item
					name={fieldName([
						'legalAndManagementCategory',
						'customerManagements',
					])}
					initialValue={
						data?.legalAndManagementCategory?.customerManagements ?? []
					}
					noStyle
				/>
				<Form.Item
					name={fieldName([
						'legalAndManagementCategory',
						'debiturOtherInformation',
					])}
					initialValue={
						data?.legalAndManagementCategory?.debiturOtherInformation
					}
					noStyle
				/>

				<FleetTitle>Legalitas {data?.businessEntity?.value}</FleetTitle>
				{LMcompanyLegalFrag()}

				<FleetTitle>Legalitas Perizinan</FleetTitle>
				{LMlicensingLegalFrag()}

				<FleetTitle>
					Legalitas Perusahaan Penjamin (apabila terdapat Penjaminan dari
					Perusahaan Lain)
				</FleetTitle>
				{LMguarantorLegalFrag()}

				<FleetTitle>Profil Pengurus</FleetTitle>
				{LMmanagerProfileFrag()}

				<FleetTitle>Informasi Lain Mengenai Debitur</FleetTitle>
				<div style={{ marginTop: 20, fontStyle: 'italic' }}>
					Informasi terkait kasus hukum perusahaan dan pribadi pengurus (jika
					ada)
				</div>
				<Form.Item
					name={fieldName([
						'legalAndManagementCategory',
						'debiturOtherInformation',
					])}
					initialValue={
						data?.legalAndManagementCategory.debiturOtherInformation
					}
					style={{ marginTop: 20, marginBottom: 20 }}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>

				<FleetTitle>Struktur Organisasi dan Tenaga Kerja</FleetTitle>
				<Form.Item
					style={{ marginTop: 20 }}
					name={fieldName([
						'legalAndManagementCategory',
						'organizationAndWorkforceDocument',
					])}
					initialValue={
						data?.legalAndManagementCategory.organizationAndWorkforceDocument
					}
					label="Upload Dokumen"
					rules={isReadOnly ? [] : [rule.required()]}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(
								fieldName([
									'legalAndManagementCategory',
									'organizationAndWorkforceDocument',
								]),
								null,
							);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/nap/financial_analysis`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});
								form.setFieldValue(
									fieldName([
										'legalAndManagementCategory',
										'organizationAndWorkforceDocument',
									]),
									{
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									},
								);
							} catch {
								form.setFieldValue(
									fieldName([
										'legalAndManagementCategory',
										'organizationAndWorkforceDocument',
									]),
									null,
								);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName([
						'legalAndManagementCategory',
						'organizationAndWorkforceAdditionalInfo',
					])}
					initialValue={
						data?.legalAndManagementCategory
							.organizationAndWorkforceAdditionalInfo
					}
					label="Informasi Tambahan"
					rules={isReadOnly ? [] : [rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				{/* NOTES */}
				<Form.Item
					name={fieldName(['legalAndManagementCategory', 'notes'])}
					initialValue={data?.legalAndManagementCategory.notes}
					label="Notes FRM"
					rules={groupName === 'FRM' ? [rule.required()] : []}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly || groupName !== 'FRM'}
					/>
				</Form.Item>
				<Divider />
				{groupName !== 'FRM' && (
					<Form.Item
						name={fieldName([
							'legalAndManagementCategory',
							'verificationNotes',
						])}
						initialValue={data?.legalAndManagementCategory?.verificationNotes}
						label="Notes CCA"
						rules={groupName === 'CCA' && !isReadOnly ? [rule.required()] : []}
					>
						<TextArea
							rows={3}
							disabled={isReadOnly || groupName !== 'CCA'}
						/>
					</Form.Item>
				)}

				<Button
					type="primary"
					htmlType="button"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 20,
					}}
					onClick={() => {
						if (isReadOnly) {
							setActiveKey('2');
						} else {
							handleNext('2');
						}
					}}
				>
					NEXT
				</Button>
			</Fragment>
		);
	};

	// TEKNIS
	const TECtechnicalFormsFrag = () => {
		const isReadOnly =
			data?.technicalCategory.action === 'View' ||
			data?.technicalCategory.action === 'Disabled';

		return (
			<Fragment>
				<FleetTitle>Pola Usaha</FleetTitle>
				<Form.Item
					name={fieldName(['technicalCategory', 'businessPatternDocument'])}
					initialValue={data?.technicalCategory.businessPatternDocument}
					label="Upload Dokumen"
					style={{ marginTop: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(
								fieldName(['technicalCategory', 'businessPatternDocument']),
								null,
							);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/nap/financial_analysis`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});
								form.setFieldValue(
									fieldName(['technicalCategory', 'businessPatternDocument']),
									{
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									},
								);
							} catch {
								form.setFieldValue(
									fieldName(['technicalCategory', 'businessPatternDocument']),
									null,
								);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName([
						'technicalCategory',
						'businessPatternAnalysisResult',
					])}
					initialValue={data?.technicalCategory.businessPatternAnalysisResult}
					label="Hasil Analisa Pola Usaha"
					rules={isReadOnly ? [] : [rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				<FleetTitle>Lokasi Kantor</FleetTitle>
				<Form.Item
					name={fieldName(['technicalCategory', 'officeLocation'])}
					initialValue={data?.technicalCategory.officeLocation}
					labelAlign="left"
				>
					<Input
						disabled
						style={{ marginTop: 20, marginBottom: 20 }}
					/>
				</Form.Item>

				<FleetTitle>Lokasi Project/Site</FleetTitle>
				<Form.Item
					name={fieldName(['technicalCategory', 'siteLocation'])}
					initialValue={data?.technicalCategory.siteLocation}
					labelAlign="left"
				>
					<Input
						disabled
						style={{ marginTop: 20, marginBottom: 20 }}
					/>
				</Form.Item>

				<FleetTitle>Proses Produksi (apabila ada)</FleetTitle>
				<Form.Item
					name={fieldName(['technicalCategory', 'productionProcess'])}
					initialValue={data?.technicalCategory.productionProcess}
					style={{ marginTop: 20, marginBottom: 20 }}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>

				<FleetTitle>Realisasi dan Proyeksi Produksi</FleetTitle>
				<Form.Item
					name={fieldName([
						'technicalCategory',
						'productionRealizationAndProjectionDocument',
					])}
					initialValue={
						data?.technicalCategory.productionRealizationAndProjectionDocument
					}
					label="Upload Dokumen"
					style={{ marginTop: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(
								fieldName([
									'technicalCategory',
									'productionRealizationAndProjectionDocument',
								]),
								null,
							);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/nap/financial_analysis`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});
								form.setFieldValue(
									fieldName([
										'technicalCategory',
										'productionRealizationAndProjectionDocument',
									]),
									{
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									},
								);
							} catch {
								form.setFieldValue(
									fieldName([
										'technicalCategory',
										'productionRealizationAndProjectionDocument',
									]),
									null,
								);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName([
						'technicalCategory',
						'productionRealizationAndProjectionAdditionalInfo',
					])}
					initialValue={
						data?.technicalCategory
							.productionRealizationAndProjectionAdditionalInfo
					}
					label="Informasi Tambahan"
					rules={isReadOnly ? [] : [rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				<FleetTitle>Utilisasi Kebutuhan dan Analisa Produksi</FleetTitle>
				<Form.Item
					name={fieldName([
						'technicalCategory',
						'needsAndAnalysisProductionUtilizationDocument',
					])}
					initialValue={
						data?.technicalCategory
							.needsAndAnalysisProductionUtilizationDocument
					}
					label="Upload Dokumen"
					style={{ marginTop: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{
							width: 260,
							backgroundColor:
								isReadOnly || groupName !== 'CCA' ? '#f3f3f3' : undefined,
						}}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly || groupName !== 'CCA'}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(
								fieldName([
									'technicalCategory',
									'needsAndAnalysisProductionUtilizationDocument',
								]),
								null,
							);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/nap/financial_analysis`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});

								form.setFieldValue(
									fieldName([
										'technicalCategory',
										'needsAndAnalysisProductionUtilizationDocument',
									]),
									{
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									},
								);
							} catch {
								form.setFieldValue(
									fieldName([
										'technicalCategory',
										'needsAndAnalysisProductionUtilizationDocument',
									]),
									null,
								);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName([
						'technicalCategory',
						'needsAndAnalysisProductionUtilizationAdditionalInfo',
					])}
					initialValue={
						data?.technicalCategory
							.needsAndAnalysisProductionUtilizationAdditionalInfo
					}
					label="Informasi Tambahan"
					rules={groupName === 'CCA' && !isReadOnly ? [rule.required()] : []}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly || groupName !== 'CCA'}
					/>
				</Form.Item>
				<Divider />
			</Fragment>
		);
	};

	const TECtradeCheckingFrag = () => {
		const isReadOnly =
			data?.technicalCategory.action === 'View' ||
			data?.technicalCategory.action === 'Disabled';

		return (
			<Fragment>
				<Row style={{ marginTop: 20, marginBottom: 20 }}>
					<Col flex="auto">
						<strong>
							<i>
								Trade checking dilakukan kepada supplier dengan hasil sebagai
								berikut:
							</i>
						</strong>
					</Col>
					<Col flex="none">
						<Button
							style={{
								borderRadius: 50,
								fontSize: 12,
								height: 24,
								fontWeight: 700,
								borderColor: '#1557BC',
								color: '#1557BC',
							}}
							icon={'+'}
							disabled={isReadOnly}
							onClick={async () => {
								const newData = await tradeCheckingSupplierModal.open();
								if (!newData) return;

								form.setFieldValue(
									fieldName([
										'technicalCategory',
										'supplierTradeVerifications',
									]),
									[
										...(value?.technicalCategory?.supplierTradeVerifications ??
											[]),
										newData,
									],
								);
							}}
						>
							Add Another
						</Button>
					</Col>
				</Row>

				<Table
					style={{ marginTop: 20 }}
					scroll={{ x: 'max-content' }}
					bordered
					pagination={false}
					dataSource={
						value?.technicalCategory?.supplierTradeVerifications?.map(
							(data, i) => {
								return {
									...data,
									key: i + 1,
								};
							},
						) ?? []
					}
					columns={[
						{
							title: 'Action',
							colSpan: 2,
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={async (e) => {
										e.preventDefault();
										if (isReadOnly) return;

										const editData =
											await tradeCheckingSupplierModal.open(record);
										if (!editData) return;

										form.setFieldValue(
											fieldName([
												'technicalCategory',
												'supplierTradeVerifications',
											]),
											value?.technicalCategory?.supplierTradeVerifications?.map(
												(data, dataIndex) => {
													return index === dataIndex ? editData : data;
												},
											),
										);
									}}
								>
									<EditOutlined />
								</a>
							),
						},
						{
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, __, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={(e) => {
										e.preventDefault();
										if (!isReadOnly) {
											form.setFieldValue(
												fieldName([
													'technicalCategory',
													'supplierTradeVerifications',
												]),
												value?.technicalCategory?.supplierTradeVerifications?.filter(
													(_, i) => index !== i,
												),
											);
										}
									}}
								>
									<DeleteOutlined />
								</a>
							),
						},
						{
							title: 'Nama Supplier',
							render: (_, data) => data.supplierName,
						},
						{
							title: 'Tanggal',
							render: (_, data) =>
								data.verificationDate &&
								dayjs(data.verificationDate).format('DD-MM-YYYY'),
						},
						{
							title: 'Contact Person',
							render: (_, data) => data.contactPerson,
						},
						{
							title: 'Hasil',
							render: (_, data) => data.verificationResult,
						},
					]}
				/>
				<Form.Item
					name={fieldName(['technicalCategory', 'supplierTradeVerifications'])}
					rules={isReadOnly ? [] : [rule.required()]}
					initialValue={data?.technicalCategory?.supplierTradeVerifications}
				>
					<Input style={{ display: 'none' }} />
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName([
						'technicalCategory',
						'supplierTradeVerificationAdditionalInfo',
					])}
					initialValue={
						data?.technicalCategory.supplierTradeVerificationAdditionalInfo
					}
					label="Informasi Tambahan"
					rules={isReadOnly ? [] : [rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />
			</Fragment>
		);
	};

	const TechnicalFrag = () => {
		const isReadOnly =
			data?.technicalCategory.action === 'View' ||
			data?.technicalCategory.action === 'Disabled';

		return (
			<Fragment>
				{TECtechnicalFormsFrag()}

				<FleetTitle>Daftar dan Trade Checking Supplier</FleetTitle>
				{TECtradeCheckingFrag()}

				{/* NOTES */}
				<Form.Item
					name={fieldName(['technicalCategory', 'notes'])}
					initialValue={data?.technicalCategory.notes}
					label="Notes FRM"
					rules={groupName === 'FRM' && !isReadOnly ? [rule.required()] : []}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly || groupName !== 'FRM'}
					/>
				</Form.Item>
				<Divider />
				{groupName !== 'FRM' && (
					<Form.Item
						name={fieldName(['technicalCategory', 'verificationNotes'])}
						initialValue={data?.technicalCategory.verificationNotes}
						label="Notes CCA"
						rules={groupName === 'CCA' && !isReadOnly ? [rule.required()] : []}
					>
						<TextArea
							rows={3}
							disabled={isReadOnly || groupName !== 'CCA'}
						/>
					</Form.Item>
				)}

				<Button
					type="primary"
					htmlType="button"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 20,
					}}
					onClick={() => {
						if (isReadOnly) {
							setActiveKey('3');
						} else {
							handleNext('3');
						}
					}}
				>
					NEXT
				</Button>
			</Fragment>
		);
	};

	// MARKETING
	const MKTcontractFrag = () => {
		const isReadOnly =
			data?.marketingCategory.action === 'View' ||
			data?.marketingCategory.action === 'Disabled';

		return (
			<Fragment>
				<Row style={{ marginTop: 20, marginBottom: 20 }}>
					<Col flex="auto" />
					<Col flex="none">
						<Button
							style={{
								borderRadius: 50,
								fontSize: 12,
								height: 24,
								fontWeight: 700,
								borderColor: '#1557BC',
								color: '#1557BC',
							}}
							icon={'+'}
							disabled={isReadOnly}
							onClick={async () => {
								const newData = await employmentContractModal.open();
								if (!newData) return;

								form.setFieldValue(
									fieldName(['marketingCategory', 'employmentContracts']),
									[
										...(value?.marketingCategory?.employmentContracts ?? []),
										newData,
									],
								);
							}}
						>
							Add Another
						</Button>
					</Col>
				</Row>
				<Table
					style={{ marginTop: 20, marginBottom: 20 }}
					scroll={{ x: 'max-content' }}
					bordered
					pagination={false}
					dataSource={
						value?.marketingCategory?.employmentContracts?.map((data, i) => {
							return {
								...data,
								key: i + 1,
							};
						}) ?? []
					}
					columns={[
						{
							title: 'Action',
							colSpan: 3,
							width: 100,
							align: 'center',
							render: (_, record) => (
								<a
									onClick={(e) => {
										e.preventDefault();
										if (!record.contractDocument) return;
										openPreviewModal(record.contractDocument as FleetFile);
									}}
								>
									<FileSearchOutlined />
								</a>
							),
						},
						{
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={async (e) => {
										e.preventDefault();
										if (isReadOnly) return;

										const editData = await employmentContractModal.open(record);
										if (!editData) return;

										form.setFieldValue(
											fieldName(['marketingCategory', 'employmentContracts']),
											value?.marketingCategory?.employmentContracts?.map(
												(data, dataIndex) => {
													return index === dataIndex ? editData : data;
												},
											),
										);
									}}
								>
									<EditOutlined />
								</a>
							),
						},
						{
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, __, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={(e) => {
										e.preventDefault();
										if (isReadOnly) return;

										form.setFieldValue(
											fieldName(['marketingCategory', 'employmentContracts']),
											value?.marketingCategory?.employmentContracts?.filter(
												(_, i) => index !== i,
											),
										);
									}}
								>
									<DeleteOutlined />
								</a>
							),
						},
						{
							title: 'Bouwheer',
							render: (_, record) => record.bouwheer.name,
						},
						{
							title: 'Bouwheer ID',
							render: (_, record) => record.bouwheer.id,
						},
						{
							title: 'Kelas Industri',
							render: (_, record) =>
								record.bouwheer.industrySector.industryClass,
						},
						{
							title: 'Nama Pekerjaan',
							render: (_, record) => record.jobTitle,
						},
						{
							title: 'No. SPK',
							render: (_, record) => record.spkNumber,
						},
						{
							title: 'Lokasi Pekerjaan',
							render: (_, record) => record.jobLocation,
						},
						{
							title: 'Detail Kontrak',
							width: 300,
							render: (_, record) => record.contractDetail,
						},
						{
							title: 'Nilai Kontrak',
							render: (_, record) =>
								formatCurrency(record.contractValue) ?? '-',
						},
						{
							title: 'Tanggal Mulai',
							render: (_, record) =>
								record.startDate &&
								dayjs(record.startDate).format('DD/MM/YYYY'),
						},
						{
							title: 'Tanggal Berakhir',
							render: (_, record) =>
								record.endDate && dayjs(record.endDate).format('DD/MM/YYYY'),
						},
						{
							title: 'Tenor',
							render: (_, record) => record.tenor,
						},
						{
							title: 'Sistem Pembayaran',
							render: (_, record) => record.paymentMethod?.value,
						},
						{
							title: 'TOP Pembayaran',
							render: (_, record) => record.termOfPayment,
						},
					]}
				/>
				<Divider />
				<Form.Item
					name={fieldName([
						'marketingCategory',
						'employmentContractAdditionalInfo',
					])}
					initialValue={
						data?.marketingCategory.employmentContractAdditionalInfo
					}
					label="Informasi Tambahan"
					style={{ marginBottom: 20 }}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
			</Fragment>
		);
	};

	const MKTsaleWorkFrag = () => {
		const isReadOnly =
			data?.marketingCategory.action === 'View' ||
			data?.marketingCategory.action === 'Disabled';

		return (
			<Fragment>
				<FleetTitle>Realisasi dan Proyeksi Penjualan/Pekerjaan</FleetTitle>
				<Form.Item
					name={fieldName([
						'marketingCategory',
						'salesAndWorkRealizationProjectionDocument',
					])}
					label="Upload Dokumen"
					initialValue={
						data?.marketingCategory.salesAndWorkRealizationProjectionDocument
					}
					rules={isReadOnly ? [] : [rule.required()]}
					style={{ marginTop: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(
								fieldName([
									'marketingCategory',
									'salesAndWorkRealizationProjectionDocument',
								]),
								null,
							);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/nap/financial_analysis`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});
								form.setFieldValue(
									fieldName([
										'marketingCategory',
										'salesAndWorkRealizationProjectionDocument',
									]),
									{
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									},
								);
							} catch {
								form.setFieldValue(
									fieldName([
										'marketingCategory',
										'salesAndWorkRealizationProjectionDocument',
									]),
									null,
								);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName([
						'marketingCategory',
						'salesAndWorkRealizationProjectionAdditionalInfo',
					])}
					initialValue={
						data?.marketingCategory
							.salesAndWorkRealizationProjectionAdditionalInfo
					}
					label="Informasi Tambahan"
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />
			</Fragment>
		);
	};

	const MKTtradeCheckingFrag = () => {
		const isReadOnly =
			data?.marketingCategory.action === 'View' ||
			data?.marketingCategory.action === 'Disabled';

		return (
			<Fragment>
				<Row style={{ marginTop: 20, marginBottom: 20 }}>
					<Col flex="auto">
						<strong>
							<i>
								Trade checking dilakukan kepada buyer dengan hasil sebagai
								berikut:
							</i>
						</strong>
					</Col>
					<Col flex="none">
						<Button
							style={{
								borderRadius: 50,
								fontSize: 12,
								height: 24,
								fontWeight: 700,
								borderColor: '#1557BC',
								color: '#1557BC',
							}}
							icon={'+'}
							disabled={isReadOnly}
							onClick={async () => {
								const newData = await tradeCheckingBouwheerModal.open();
								if (!newData) return;

								form.setFieldValue(
									fieldName([
										'marketingCategory',
										'bouwheerTradeVerifications',
									]),
									[
										...(value?.marketingCategory?.bouwheerTradeVerifications ??
											[]),
										newData,
									],
								);
							}}
						>
							Add Another
						</Button>
					</Col>
				</Row>

				<Table
					style={{ marginTop: 20 }}
					scroll={{ x: 'max-content' }}
					bordered
					pagination={false}
					dataSource={
						value?.marketingCategory?.bouwheerTradeVerifications?.map(
							(data, i) => {
								return {
									...data,
									key: i + 1,
								};
							},
						) ?? []
					}
					columns={[
						{
							title: 'Action',
							colSpan: 2,
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={async (e) => {
										e.preventDefault();
										if (isReadOnly) return;

										const editData =
											await tradeCheckingBouwheerModal.open(record);
										if (!editData) return;

										form.setFieldValue(
											fieldName([
												'marketingCategory',
												'bouwheerTradeVerifications',
											]),
											value?.marketingCategory?.bouwheerTradeVerifications?.map(
												(data, dataIndex) => {
													return index === dataIndex ? editData : data;
												},
											),
										);
									}}
								>
									<EditOutlined />
								</a>
							),
						},
						{
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, __, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={(e) => {
										e.preventDefault();
										if (isReadOnly) return;

										form.setFieldValue(
											fieldName([
												'marketingCategory',
												'bouwheerTradeVerifications',
											]),
											value?.marketingCategory?.bouwheerTradeVerifications?.filter(
												(_, i) => index !== i,
											),
										);
									}}
								>
									<DeleteOutlined />
								</a>
							),
						},
						{
							title: 'Nama Supplier',
							render: (_, data) => data.supplierName,
						},
						{
							title: 'Tanggal',
							render: (_, data) =>
								data.verificationDate &&
								dayjs(data.verificationDate).format('DD-MM-YYYY'),
						},
						{
							title: 'Contact Person',
							render: (_, data) => data.contactPerson,
						},
						{
							title: 'Hasil',
							render: (_, data) => data.verificationResult,
						},
					]}
				/>
				<Form.Item
					name={fieldName(['marketingCategory', 'bouwheerTradeVerifications'])}
					rules={isReadOnly ? [] : [rule.required()]}
					initialValue={data?.marketingCategory.bouwheerTradeVerifications}
				>
					<Input style={{ display: 'none' }} />
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName([
						'marketingCategory',
						'bouwheerTradeVerificationAdditionalInfo',
					])}
					initialValue={
						data?.marketingCategory.bouwheerTradeVerificationAdditionalInfo
					}
					label="Informasi Tambahan"
					rules={isReadOnly ? [] : [rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />
			</Fragment>
		);
	};

	const MarketingFrag = () => {
		const isReadOnly =
			data?.marketingCategory.action === 'View' ||
			data?.marketingCategory.action === 'Disabled';

		return (
			<Fragment>
				<Form.Item
					name={fieldName(['marketingCategory', 'employmentContracts'])}
					initialValue={data?.marketingCategory?.employmentContracts ?? []}
					noStyle
				/>

				<FleetTitle>List Kontrak Kerja</FleetTitle>
				{MKTcontractFrag()}

				{MKTsaleWorkFrag()}

				<FleetTitle>Trade Checking Bouwheer/Buyer</FleetTitle>
				{MKTtradeCheckingFrag()}

				{/* NOTES */}
				<Form.Item
					name={fieldName(['marketingCategory', 'notes'])}
					initialValue={data?.marketingCategory.notes}
					label="Notes FRM"
					rules={groupName === 'FRM' && !isReadOnly ? [rule.required()] : []}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly || groupName !== 'FRM'}
					/>
				</Form.Item>
				<Divider />
				{groupName !== 'FRM' && (
					<Form.Item
						name={fieldName(['marketingCategory', 'verificationNotes'])}
						initialValue={data?.marketingCategory.verificationNotes}
						label="Notes CCA"
						rules={groupName === 'CCA' && !isReadOnly ? [rule.required()] : []}
					>
						<TextArea
							rows={3}
							disabled={isReadOnly || groupName !== 'CCA'}
						/>
					</Form.Item>
				)}

				<Button
					type="primary"
					htmlType="button"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 20,
					}}
					onClick={() => {
						if (isReadOnly) {
							setActiveKey('4');
						} else {
							handleNext('4');
						}
					}}
				>
					NEXT
				</Button>
			</Fragment>
		);
	};

	// KEUANGAN
	const FINinfoKapFrag = () => {
		const isReadOnly =
			data?.financialCategory.action === 'View' ||
			data?.financialCategory.action === 'Disabled';

		return (
			<Fragment>
				<Row style={{ marginTop: 20, marginBottom: 20 }}>
					<Col flex="auto" />
					<Col flex="none">
						<Button
							style={{
								borderRadius: 50,
								fontSize: 12,
								height: 24,
								fontWeight: 700,
								borderColor: '#1557BC',
								color: '#1557BC',
							}}
							icon={'+'}
							disabled={isReadOnly}
							onClick={async () => {
								const newData = await kapModal.open();
								if (!newData) return;

								form.setFieldValue(
									fieldName(['financialCategory', 'kapInformations']),
									[
										...(value?.financialCategory?.kapInformations ?? []),
										newData,
									],
								);
							}}
						>
							Add Another
						</Button>
					</Col>
				</Row>

				<Form.Item
					name={fieldName(['financialCategory', 'kapInformations'])}
					initialValue={data?.financialCategory?.kapInformations ?? []}
					noStyle
				/>

				<Table
					style={{ marginTop: 20 }}
					scroll={{ x: 'max-content' }}
					bordered
					pagination={false}
					dataSource={
						value?.financialCategory?.kapInformations?.map((data, i) => {
							return {
								...data,
								key: i + 1,
							};
						}) ?? []
					}
					columns={[
						{
							title: 'Action',
							colSpan: 2,
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={async (e) => {
										e.preventDefault();
										if (isReadOnly) return;

										const editData = await kapModal.open(record);
										if (!editData) return;

										form.setFieldValue(
											fieldName(['financialCategory', 'kapInformations']),
											value?.financialCategory?.kapInformations?.map(
												(data, dataIndex) => {
													return index === dataIndex ? editData : data;
												},
											),
										);
									}}
								>
									<EditOutlined />
								</a>
							),
						},
						{
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, __, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={(e) => {
										e.preventDefault();
										if (isReadOnly) return;

										form.setFieldValue(
											fieldName(['financialCategory', 'kapInformations']),
											value?.financialCategory?.kapInformations?.filter(
												(_, i) => index !== i,
											),
										);
									}}
								>
									<DeleteOutlined />
								</a>
							),
						},
						{
							title: 'Tahun',
							render: (_, record) => record.year,
						},
						{
							title: 'KAP',
							render: (_, record) => record.kap,
						},
						{
							title: 'Opini',
							render: (_, record) => record.opinion,
						},
						{
							title: 'Keterangan',
							render: (_, record) => record.description,
						},
					]}
				/>
				<Form.Item
					name={fieldName(['financialCategory', 'kapInformations'])}
					rules={isReadOnly ? [] : [rule.required()]}
					initialValue={data?.financialCategory?.kapInformations}
				>
					<Input style={{ display: 'none' }} />
				</Form.Item>
			</Fragment>
		);
	};

	const FinancialFrag = () => {
		const isReadOnly =
			data?.financialCategory.action === 'View' ||
			data?.financialCategory.action === 'Disabled';

		return (
			<Fragment>
				<FleetTitle>Informasi KAP</FleetTitle>
				{FINinfoKapFrag()}

				<FleetTitle>Neraca</FleetTitle>
				<Form.Item
					name={fieldName(['financialCategory', 'balanceSheetDocument'])}
					label="Upload Neraca"
					initialValue={data?.financialCategory?.balanceSheetDocument}
					rules={isReadOnly ? [] : [rule.required()]}
					style={{ marginTop: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(
								fieldName(['financialCategory', 'balanceSheetDocument']),
								null,
							);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/nap/financial_analysis`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});
								form.setFieldValue(
									fieldName(['financialCategory', 'balanceSheetDocument']),
									{
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									},
								);
							} catch {
								form.setFieldValue(
									fieldName(['financialCategory', 'balanceSheetDocument']),
									null,
								);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName(['financialCategory', 'balanceSheetAnalysisResult'])}
					label="Hasil Analisa Neraca"
					initialValue={data?.financialCategory?.balanceSheetAnalysisResult}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				<FleetTitle>Laba Rugi</FleetTitle>
				<Form.Item
					name={fieldName(['financialCategory', 'profitAndLossDocument'])}
					label="Upload Laba Rugi"
					initialValue={data?.financialCategory?.profitAndLossDocument}
					rules={isReadOnly ? [] : [rule.required()]}
					style={{ marginTop: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						beforeUpload={() => false}
						disabled={isReadOnly}
						onRemove={() => {
							form.setFieldValue(
								fieldName(['financialCategory', 'profitAndLossDocument']),
								null,
							);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/nap/financial_analysis`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});
								form.setFieldValue(
									fieldName(['financialCategory', 'profitAndLossDocument']),
									{
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									},
								);
							} catch {
								form.setFieldValue(
									fieldName(['financialCategory', 'profitAndLossDocument']),
									null,
								);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName(['financialCategory', 'profitAndLossAnalysisResult'])}
					label="Hasil Analisa Laba Rugi"
					initialValue={data?.financialCategory?.profitAndLossAnalysisResult}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				<FleetTitle>Rasio Keuangan</FleetTitle>
				<Form.Item
					name={fieldName(['financialCategory', 'financialRatioDocument'])}
					label="Upload Rasio Keuangan"
					initialValue={data?.financialCategory?.financialRatioDocument}
					rules={isReadOnly ? [] : [rule.required()]}
					style={{ marginTop: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(
								fieldName(['financialCategory', 'financialRatioDocument']),
								null,
							);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/nap/financial_analysis`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});
								form.setFieldValue(
									fieldName(['financialCategory', 'financialRatioDocument']),
									{
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									},
								);
							} catch {
								form.setFieldValue(
									fieldName(['financialCategory', 'financialRatioDocument']),
									null,
								);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName([
						'financialCategory',
						'financialRatiosAnalysisResult',
					])}
					label="Hasil Analisa Rasio Keuangan"
					initialValue={data?.financialCategory?.financialRatiosAnalysisResult}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				<FleetTitle>Analisa Rekening Koran</FleetTitle>
				<div style={{ marginTop: 20, marginBottom: 20, fontStyle: 'italic' }}>
					Jumlah mutasi kredit dan debit, saldo rata-rata, tolakan kliring,
					saldo akhir, transaksi tidak biasa, dsb.
				</div>

				<Form.Item
					name={fieldName([
						'financialCategory',
						'bankStatementAnalysisDocument',
					])}
					label="Upload Analisa Rekening Koran"
					initialValue={data?.financialCategory?.bankStatementAnalysisDocument}
					rules={isReadOnly ? [] : [rule.required()]}
					style={{ marginTop: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(
								fieldName([
									'financialCategory',
									'bankStatementAnalysisDocument',
								]),
								null,
							);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/nap/financial_analysis`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});
								form.setFieldValue(
									fieldName([
										'financialCategory',
										'bankStatementAnalysisDocument',
									]),
									{
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									},
								);
							} catch {
								form.setFieldValue(
									fieldName([
										'financialCategory',
										'bankStatementAnalysisDocument',
									]),
									null,
								);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName([
						'financialCategory',
						'bankStatementAnalysisAdditionalInfo',
					])}
					label="Informasi Tambahan"
					initialValue={
						data?.financialCategory?.bankStatementAnalysisAdditionalInfo
					}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				<FleetTitle>Analisa Pembiayaan (Repayment Capacity)</FleetTitle>
				<Form.Item
					name={fieldName(['financialCategory', 'repaymentCapacityDocument'])}
					label="Upload Repayment Capacity"
					initialValue={data?.financialCategory?.repaymentCapacityDocument}
					rules={isReadOnly ? [] : [rule.required()]}
					style={{ marginTop: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(
								fieldName(['financialCategory', 'repaymentCapacityDocument']),
								null,
							);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/nap/financial_analysis`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});
								form.setFieldValue(
									fieldName(['financialCategory', 'repaymentCapacityDocument']),
									{
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									},
								);
							} catch {
								form.setFieldValue(
									fieldName(['financialCategory', 'repaymentCapacityDocument']),
									null,
								);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName([
						'financialCategory',
						'repaymentCapacityAdditionalInfo',
					])}
					label="Informasi Tambahan"
					initialValue={
						data?.financialCategory?.repaymentCapacityAdditionalInfo
					}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				<FleetTitle>
					Proyeksi Cashflow (termasuk asumsi yang digunakan untuk proyeksi
					keuangan)
				</FleetTitle>
				<Form.Item
					name={fieldName(['financialCategory', 'cashFlowProjectionDocument'])}
					label="Upload Proyeksi Cashflow"
					initialValue={data?.financialCategory?.cashFlowProjectionDocument}
					rules={isReadOnly ? [] : [rule.required()]}
					style={{ marginTop: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(
								fieldName(['financialCategory', 'cashFlowProjectionDocument']),
								null,
							);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/nap/financial_analysis`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});
								form.setFieldValue(
									fieldName([
										'financialCategory',
										'cashFlowProjectionDocument',
									]),
									{
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									},
								);
							} catch {
								form.setFieldValue(
									fieldName([
										'financialCategory',
										'cashFlowProjectionDocument',
									]),
									null,
								);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName([
						'financialCategory',
						'cashFlowProjectionAdditionalInfo',
					])}
					label="Informasi Tambahan"
					initialValue={
						data?.financialCategory?.cashFlowProjectionAdditionalInfo
					}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				<FleetTitle>Evaluasi Guarantor apabila ada CG/PG</FleetTitle>
				<Form.Item
					name={fieldName(['financialCategory', 'guarantorLetterDocument'])}
					label="Upload Surat Penjamin"
					initialValue={data?.financialCategory?.guarantorLetterDocument}
					style={{ marginTop: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(
								fieldName(['financialCategory', 'guarantorLetterDocument']),
								null,
							);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/nap/financial_analysis`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});
								form.setFieldValue(
									fieldName(['financialCategory', 'guarantorLetterDocument']),
									{
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									},
								);
							} catch {
								form.setFieldValue(
									fieldName(['financialCategory', 'guarantorLetterDocument']),
									null,
								);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName([
						'financialCategory',
						'guarantorEvaluationAdditionalInfo',
					])}
					label="Informasi Tambahan"
					initialValue={
						data?.financialCategory?.guarantorEvaluationAdditionalInfo
					}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				{/* NOTES */}
				<Form.Item
					name={fieldName(['financialCategory', 'notes'])}
					initialValue={data?.financialCategory.notes}
					label="Notes FRM"
					rules={groupName === 'FRM' && !isReadOnly ? [rule.required()] : []}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly || groupName !== 'FRM'}
					/>
				</Form.Item>
				<Divider />
				{groupName !== 'FRM' && (
					<Form.Item
						name={fieldName(['financialCategory', 'verificationNotes'])}
						initialValue={data?.financialCategory.verificationNotes}
						label="Notes CCA"
						rules={groupName === 'CCA' && !isReadOnly ? [rule.required()] : []}
					>
						<TextArea
							rows={3}
							disabled={isReadOnly || groupName !== 'CCA'}
						/>
					</Form.Item>
				)}

				<Button
					type="primary"
					htmlType="button"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 20,
					}}
					disabled={
						submitNAPAnalysisMutation.isPending ||
						verifyNAPAnalysisMutation.isPending
					}
					loading={
						submitNAPAnalysisMutation.isPending ||
						verifyNAPAnalysisMutation.isPending
					}
					onClick={() => handleNext('submit')}
				>
					NEXT
				</Button>
			</Fragment>
		);
	};

	return (
		<Fragment>
			<LegalDocumentModal
				customerId={customerId}
				prospectId={prospectId}
				{...legalDocumentModal}
			/>
			<BusinessDocumentModal
				customerId={customerId}
				prospectId={prospectId}
				{...businessDocumentModal}
			/>
			<ManagerProfileModal {...managerProfileModal} />
			<TradeCheckingSupplierModal {...tradeCheckingSupplierModal} />
			<EmploymentContractModal
				customerId={customerId}
				prospectId={prospectId}
				{...employmentContractModal}
			/>
			<TradeCheckingBouwheerModal {...tradeCheckingBouwheerModal} />
			<KAPModal {...kapModal} />
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{
					style: {
						width: 200,
					},
				}}
				onFinish={async (values) => {
					const napAnalysisPayload: NAPAnalysisPayload = {
						napId: values?.napId,
						legalAndManagementCategory: {
							customerLegalDocuments:
								values?.legalAndManagementCategory?.customerLegalDocuments?.map(
									(document) => ({
										id: document.id,
										file: document?.file?.id
											? {
													id: document.file.id,
													name: document.file.name,
													path: document.file.path,
												}
											: undefined!,
										documentNo: document.documentNo,
										documentDate: document.documentDate,
										notes: document.notes,
										boardOfDirectorsAuthority:
											document.boardOfDirectorsAuthority,
										clause: document.clause,
									}),
								) ?? [],
							customerBusinessDocuments:
								values?.legalAndManagementCategory?.customerBusinessDocuments?.map(
									(document) => ({
										id: document.id,
										file: document?.file?.id
											? {
													id: document.file.id,
													name: document.file.name,
													path: document.file.path,
												}
											: undefined!,
										documentNo: document.documentNo,
										documentDate: document.documentDate,
										publisherId: document.publishers?.[0]?.id,
										validityPeriodId: document.validityPeriods?.[0]?.id,
										notes: document.notes,
									}),
								) ?? [],
							customerManagements:
								values?.legalAndManagementCategory?.customerManagements?.map(
									(management) => ({
										id: management.id,
										customerId: customerId,
										name: management.name,
										citizenship: management.citizenship?.id,
										nationalityId: management.nationality?.id,
										identityCardType: management.identityCardType?.id,
										identityNumber: management.identityNumber,
										dateOfBirth: management.dateOfBirth,
										position: management.position?.id,
										address: management.address,
										rt: management.rt,
										rw: management.rw,
										postalId: management.postal?.postalId,
										phoneNumber: management.phoneNumber,
										status: management.status?.id,
										identityFile: management?.identityFile && {
											id: management.identityFile?.id,
											name: management.identityFile?.name,
											path: management.identityFile?.path,
										},
										isShareholder: management.isShareholder,
										shareholding: management.shareholding,
										startManagementPeriod: management.startManagementPeriod,
										endManagementPeriod: management.endManagementPeriod,
										profesionalExperience: management.profesionalExperience,
									}),
								) ?? [],
							guarantorCompanyLegalityDocuments:
								values?.legalAndManagementCategory?.guarantorCompanyLegalityDocuments?.map(
									(document) => ({
										id: document.id,
										documentType: document.documentType,
										documentNo: document.documentNo,
										documentDate: document.documentDate,
										file: {
											id: document.file?.id,
											name: document.file?.name,
											path: document.file?.path,
										},
										isActive: document.isActive,
										notes: document.notes,
									}),
								) ?? [],
							legalCaseInformation:
								values?.legalAndManagementCategory?.legalCaseInformation,
							organizationAndWorkforceDocument: values
								?.legalAndManagementCategory
								?.organizationAndWorkforceDocument && {
								id: values.legalAndManagementCategory
									.organizationAndWorkforceDocument?.id,
								name: values.legalAndManagementCategory
									.organizationAndWorkforceDocument?.name,
								path: values.legalAndManagementCategory
									.organizationAndWorkforceDocument?.path,
							},
							organizationAndWorkforceAdditionalInfo:
								values?.legalAndManagementCategory
									?.organizationAndWorkforceAdditionalInfo,
							debiturOtherInformation:
								values?.legalAndManagementCategory?.debiturOtherInformation,
							notes: values?.legalAndManagementCategory?.notes,
							verificationNotes:
								values?.legalAndManagementCategory?.verificationNotes,
						},
						technicalCategory: {
							businessPatternDocument: values?.technicalCategory
								?.businessPatternDocument && {
								id: values.technicalCategory.businessPatternDocument?.id,
								name: values.technicalCategory.businessPatternDocument?.name,
								path: values.technicalCategory.businessPatternDocument?.path,
							},
							businessPatternAnalysisResult:
								values?.technicalCategory?.businessPatternAnalysisResult,
							productionProcess: values?.technicalCategory?.productionProcess,
							productionRealizationAndProjectionDocument: values
								?.technicalCategory
								?.productionRealizationAndProjectionDocument && {
								id: values.technicalCategory
									.productionRealizationAndProjectionDocument?.id,
								name: values.technicalCategory
									.productionRealizationAndProjectionDocument?.name,
								path: values.technicalCategory
									.productionRealizationAndProjectionDocument?.path,
							},
							productionRealizationAndProjectionAdditionalInfo:
								values?.technicalCategory
									?.productionRealizationAndProjectionAdditionalInfo,
							needsAndAnalysisProductionUtilizationDocument: values
								?.technicalCategory
								?.needsAndAnalysisProductionUtilizationDocument && {
								id: values.technicalCategory
									.needsAndAnalysisProductionUtilizationDocument?.id,
								name: values.technicalCategory
									.needsAndAnalysisProductionUtilizationDocument?.name,
								path: values.technicalCategory
									.needsAndAnalysisProductionUtilizationDocument?.path,
							},
							needsAndAnalysisProductionUtilizationAdditionalInfo:
								values?.technicalCategory
									?.needsAndAnalysisProductionUtilizationAdditionalInfo,
							supplierTradeVerifications:
								values?.technicalCategory?.supplierTradeVerifications?.map(
									(verification) => ({
										id: verification.id,
										supplierName: verification.supplierName,
										verificationDate: verification.verificationDate,
										contactPerson: verification.contactPerson,
										verificationResult: verification.verificationResult,
									}),
								) ?? [],
							supplierTradeVerificationAdditionalInfo:
								values?.technicalCategory
									?.supplierTradeVerificationAdditionalInfo || '',
							notes: values?.technicalCategory?.notes,
							verificationNotes: values?.technicalCategory?.verificationNotes,
						},
						marketingCategory: {
							employmentContracts:
								values?.marketingCategory?.employmentContracts.map(
									(contract) => ({
										id: contract.id,
										prospectId: contract.prospectId,
										bouwheerId: contract.bouwheer.id,
										jobTitle: contract.jobTitle,
										spkNumber: contract.spkNumber,
										jobLocation: contract.jobLocation,
										termOfPayment: contract.termOfPayment,
										contractDocument: contract.contractDocument && {
											id: contract.contractDocument?.id,
											name: contract.contractDocument?.name,
											path: contract.contractDocument?.path,
										},
										contractDetail: contract.contractDetail,
										contractValue: contract.contractValue,
										startDate: contract.startDate,
										endDate: contract.endDate,
										tenor: contract.tenor,
										paymentMethod: contract.paymentMethod?.id,
									}),
								) ?? [],
							employmentContractAdditionalInfo:
								values?.marketingCategory?.employmentContractAdditionalInfo,
							salesAndWorkRealizationProjectionDocument: values
								?.marketingCategory
								?.salesAndWorkRealizationProjectionDocument && {
								id: values.marketingCategory
									.salesAndWorkRealizationProjectionDocument?.id,
								name: values.marketingCategory
									.salesAndWorkRealizationProjectionDocument?.name,
								path: values.marketingCategory
									.salesAndWorkRealizationProjectionDocument?.path,
							},
							salesAndWorkRealizationProjectionAdditionalInfo:
								values?.marketingCategory
									?.salesAndWorkRealizationProjectionAdditionalInfo,
							bouwheerTradeVerifications:
								values?.marketingCategory?.bouwheerTradeVerifications?.map(
									(verification) => ({
										id: verification.id,
										supplierName: verification.supplierName,
										verificationDate: verification.verificationDate,
										contactPerson: verification.contactPerson,
										verificationResult: verification.verificationResult,
									}),
								) ?? [],
							bouwheerTradeVerificationAdditionalInfo:
								values?.marketingCategory
									?.bouwheerTradeVerificationAdditionalInfo || '',
							notes: values?.marketingCategory?.notes,
							verificationNotes: values?.marketingCategory?.verificationNotes,
						},
						financialCategory: {
							kapInformations: values?.financialCategory?.kapInformations,
							balanceSheetDocument: values?.financialCategory
								?.balanceSheetDocument && {
								id: values.financialCategory.balanceSheetDocument?.id,
								name: values.financialCategory.balanceSheetDocument?.name,
								path: values.financialCategory.balanceSheetDocument?.path,
							},
							balanceSheetAnalysisResult:
								values?.financialCategory?.balanceSheetAnalysisResult,
							profitAndLossDocument: values?.financialCategory
								?.profitAndLossDocument && {
								id: values.financialCategory.profitAndLossDocument?.id,
								name: values.financialCategory.profitAndLossDocument?.name,
								path: values.financialCategory.profitAndLossDocument?.path,
							},
							profitAndLossAnalysisResult:
								values?.financialCategory?.profitAndLossAnalysisResult,
							financialRatioDocument: values?.financialCategory
								?.financialRatioDocument && {
								id: values.financialCategory.financialRatioDocument?.id,
								name: values.financialCategory.financialRatioDocument?.name,
								path: values.financialCategory.financialRatioDocument?.path,
							},
							financialRatiosAnalysisResult:
								values?.financialCategory?.financialRatiosAnalysisResult,
							bankStatementAnalysisDocument: values?.financialCategory
								?.bankStatementAnalysisDocument && {
								id: values.financialCategory.bankStatementAnalysisDocument?.id,
								name: values.financialCategory.bankStatementAnalysisDocument
									?.name,
								path: values.financialCategory.bankStatementAnalysisDocument
									?.path,
							},
							bankStatementAnalysisAdditionalInfo:
								values?.financialCategory?.bankStatementAnalysisAdditionalInfo,
							repaymentCapacityDocument: values?.financialCategory
								?.repaymentCapacityDocument && {
								id: values.financialCategory.repaymentCapacityDocument?.id,
								name: values.financialCategory.repaymentCapacityDocument?.name,
								path: values.financialCategory.repaymentCapacityDocument?.path,
							},
							repaymentCapacityAdditionalInfo:
								values?.financialCategory?.repaymentCapacityAdditionalInfo,
							guarantorLetterDocument: values?.financialCategory
								?.guarantorLetterDocument && {
								id: values.financialCategory.guarantorLetterDocument?.id,
								name: values.financialCategory.guarantorLetterDocument?.name,
								path: values.financialCategory.guarantorLetterDocument?.path,
							},
							guarantorEvaluationAdditionalInfo:
								values?.financialCategory?.guarantorEvaluationAdditionalInfo,
							cashFlowProjectionDocument: values?.financialCategory
								?.cashFlowProjectionDocument && {
								id: values.financialCategory.cashFlowProjectionDocument.id,
								name: values.financialCategory.cashFlowProjectionDocument.name,
								path: values.financialCategory.cashFlowProjectionDocument.path,
							},
							cashFlowProjectionAdditionalInfo:
								values?.financialCategory?.cashFlowProjectionAdditionalInfo,
							notes: values?.financialCategory?.notes,
							verificationNotes: values?.financialCategory?.verificationNotes,
						},
					};

					if (groupName === 'FRM') {
						await submitNAPAnalysisMutation.mutateAsync(napAnalysisPayload);
					} else if (groupName === 'CCA') {
						const verifyNAPAnalysisPayload = {
							...napAnalysisPayload,
							id: data?.financialAnalysisId,
						};
						await verifyNAPAnalysisMutation.mutateAsync(
							verifyNAPAnalysisPayload,
						);
					}

					onSubmitted();
				}}
			>
				<Form.Item
					name={fieldName(['financialAnalysisId'])}
					initialValue={data?.financialAnalysisId}
					noStyle
				/>
				<Form.Item
					name={fieldName('napId')}
					initialValue={data?.napId}
					noStyle
				/>
				<Tabs
					activeKey={activeKey}
					onChange={setActiveKey}
					items={[
						{
							key: '1',
							label: 'Aspek Legal dan Management',
							children: LegalManagementFrag(),
						},
						{
							key: '2',
							label: 'Aspek Teknis',
							children: TechnicalFrag(),
						},
						{
							key: '3',
							label: 'Aspek Pemasaran',
							children: MarketingFrag(),
						},
						{
							key: '4',
							label: 'Aspek Keuangan',
							children: FinancialFrag(),
						},
					]}
				/>
			</Form>
		</Fragment>
	);
};

export default NAPAnalysisForm;
